import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getDirectionMultiplier,
  isRTL,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
const DRAG_THRESHOLD = 5;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CircularHero {
  #host;
  #track;
  #slides;
  #position = 0;
  #frame = 0;
  #timer;
  #resumeAt = 0;
  #gesture = null;
  #clickUntil = 0;
  #controller = new AbortController();
  #originalTabIndex;
  #originalLive;
  #touchEvents = 'ontouchstart' in window;

  constructor(host) {
    this.#host = host;
    this.#track = host.refs.scroller;
    this.#slides = host.slides;
    this.#position = this.#mod(host.initialSlideIndex);
    this.#originalTabIndex = this.#track.getAttribute('tabindex');
    this.#originalLive = host.getAttribute('aria-live');
    host.setAttribute('data-circular-hero', '');
    host.setAttribute('aria-live', 'off');
    this.#track.setAttribute('tabindex', '0');
    this.#track.scrollLeft = 0;
    const { signal } = this.#controller;
    this.#track.addEventListener('pointerdown', this.#pointerDown, { signal });
    if (this.#touchEvents) {
      this.#track.addEventListener('touchstart', this.#touchStart, { signal, passive: true });
      this.#track.addEventListener('touchmove', this.#touchMove, { signal, passive: false });
      this.#track.addEventListener('touchend', this.#touchEnd, { signal });
      this.#track.addEventListener('touchcancel', this.#touchEnd, { signal });
    }
    document.addEventListener('pointermove', this.#pointerMove, { signal, passive: false });
    document.addEventListener('pointerup', this.#pointerEnd, { signal });
    document.addEventListener('pointercancel', this.#pointerEnd, { signal });
    this.#track.addEventListener('lostpointercapture', this.#pointerEnd, { signal });
    this.#track.addEventListener('dragstart', preventDefault, { signal });
    host.addEventListener('click', this.#click, { signal, capture: true });
    host.addEventListener('keydown', this.#key, { signal });
    host.addEventListener('mouseenter', this.resume, { signal });
    host.addEventListener('mouseleave', this.resume, { signal });
    host.addEventListener('focusin', this.suspend, { signal });
    host.addEventListener('focusout', this.#focusOut, { signal });
    document.addEventListener('visibilitychange', this.resume, { signal });
    this.#render();
    this.#commit(false);
    this.resume();
  }

  #mod(value) {
    return ((value % this.#slides.length) + this.#slides.length) % this.#slides.length;
  }

  #render() {
    const count = this.#slides.length;
    for (const [index, slide] of this.#slides.entries()) {
      const offset = this.#mod(index - this.#position + count / 2) - count / 2;
      slide.style.transform = `translate3d(${offset * 100}%, 0, 0)`;
    }
  }

  #commit(userInitiated) {
    const index = this.#mod(Math.round(this.#position));
    const previousIndex = this.#host.current;
    this.#host.current = index;
    for (const [i, slide] of this.#slides.entries()) {
      slide.inert = i !== index;
      slide.setAttribute('aria-hidden', String(i !== index));
    }
    if (index !== previousIndex) {
      const slide = this.#slides[index];
      this.#host.dispatchEvent(new SlideshowSelectEvent({
        index, previousIndex, userInitiated, trigger: 'select', slide, id: slide.getAttribute('slide-id'),
      }));
    }
  }

  #move(target, animate = true, userInitiated = false) {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    const from = this.#position;
    const start = performance.now();
    const duration = animate && !prefersReducedMotion() ? 300 : 0;
    const tick = (now) => {
      const progress = duration ? Math.min(1, (now - start) / duration) : 1;
      this.#position = from + (target - from) * (1 - (1 - progress) ** 3);
      this.#render();
      if (progress < 1) {
        this.#frame = requestAnimationFrame(tick);
      } else {
        this.#frame = 0;
        this.#position = this.#mod(target);
        this.#render();
        this.#host.removeAttribute('transitioning');
        this.#commit(userInitiated);
      }
    };
    this.#host.setAttribute('transitioning', '');
    tick(start);
  }

  select(input, event, { animate = true } = {}) {
    if (event?.type === 'click' && event.detail !== 0 && performance.now() < this.#clickUntil) return;
    const requested = typeof input === 'object'
      ? this.#slides.findIndex((slide) => slide.getAttribute('slide-id') === input.id)
      : Number(input);
    if (!Number.isFinite(requested) || (typeof input === 'object' && requested < 0)) return;
    const index = this.#mod(requested);
    if (index === this.#mod(Math.round(this.#position)) && event?.type === 'click') return;
    if (index === this.#host.current && !this.#frame && !this.#gesture) return;
    event?.preventDefault();
    let delta = index - this.#mod(Math.round(this.#position));
    if (delta > this.#slides.length / 2) delta -= this.#slides.length;
    if (delta < -this.#slides.length / 2) delta += this.#slides.length;
    if (event) this.#resumeAt = performance.now() + 8000;
    this.#move(Math.round(this.#position) + delta, animate, !!event);
    this.resume();
  }

  suspend = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  };

  pause() {
    this.#host.paused = true;
    this.suspend();
  }

  play() {
    this.#host.paused = false;
    this.resume();
  }

  resume = () => {
    this.suspend();
    const host = this.#host;
    if (!host.isConnected || !host.autoplay || host.paused || document.hidden || this.#gesture || prefersReducedMotion()) return;
    if (host.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
    if (host.matches(':hover') && window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const delay = this.#resumeAt > performance.now() ? this.#resumeAt - performance.now() : host.autoplayInterval;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.#move(Math.round(this.#position) + 1);
      this.resume();
    }, delay);
  };

  #focusOut = () => queueMicrotask(this.resume);

  #touchStart = (event) => {
    if (event.touches.length !== 1) {
      if (this.#gesture) this.#pointerEnd({ pointerId: this.#gesture.id, type: 'pointercancel' });
      return;
    }
    const touch = event.touches[0];
    this.#pointerDown({
      pointerId: touch.identifier, clientX: touch.clientX, clientY: touch.clientY,
      isPrimary: true, button: 0, target: event.target, touchGesture: true,
    });
  };

  #touchMove = (event) => {
    if (event.touches.length !== 1 || !this.#gesture?.touch) return;
    const touch = Array.from(event.touches).find((item) => item.identifier === this.#gesture.id);
    if (!touch) return;
    this.#pointerMove({
      pointerId: touch.identifier, clientX: touch.clientX, clientY: touch.clientY,
      preventDefault: () => { if (event.cancelable) event.preventDefault(); },
    });
  };

  #touchEnd = (event) => {
    if (!this.#gesture?.touch) return;
    const touch = Array.from(event.changedTouches).find((item) => item.identifier === this.#gesture.id);
    if (!touch) return;
    this.#pointerEnd({ pointerId: touch.identifier, type: event.type === 'touchend' ? 'pointerup' : 'pointercancel' });
  };

  #pointerDown = (event) => {
    if (this.#touchEvents && event.pointerType === 'touch') return;
    if (!event.isPrimary) {
      if (this.#gesture) this.#pointerEnd({ pointerId: this.#gesture.id, type: 'pointercancel' });
      return;
    }
    if (event.button !== 0 || this.#host.disabled || event.target.closest('input, select, textarea, model-viewer')) return;
    const width = this.#track.clientWidth;
    if (!width) return;
    this.suspend();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#host.removeAttribute('transitioning');
    this.#clickUntil = 0;
    this.#gesture = {
      id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX,
      lastTime: performance.now(), velocity: 0, from: this.#position,
      base: Math.round(this.#position), width, moved: false, touch: !!event.touchGesture,
    };
  };

  #pointerMove = (event) => {
    if (this.#touchEvents && event.pointerType === 'touch') return;
    const gesture = this.#gesture;
    if (!gesture || event.pointerId !== gesture.id) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.moved) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
      if (Math.abs(dy) > Math.abs(dx) * 1.5 && Math.abs(dy) >= 12) {
        this.#pointerEnd({ pointerId: gesture.id, type: 'pointercancel' });
        return;
      }
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
      gesture.moved = true;
      if (!gesture.touch) this.#track.setPointerCapture(event.pointerId);
      this.#host.setAttribute('dragging', '');
    }
    event.preventDefault();
    const now = performance.now();
    const elapsed = now - gesture.lastTime;
    if (elapsed > 0) gesture.velocity = (event.clientX - gesture.lastX) / elapsed;
    gesture.lastX = event.clientX;
    gesture.lastTime = now;
    this.#position = gesture.from - clamp(dx / gesture.width, -1, 1);
    this.#render();
  };

  #pointerEnd = (event) => {
    if (this.#touchEvents && event.pointerType === 'touch') return;
    if (event.type === 'lostpointercapture' && event.target !== this.#track) return;
    const gesture = this.#gesture;
    if (!gesture || event.pointerId !== gesture.id) return;
    this.#gesture = null;
    this.#host.removeAttribute('dragging');
    if (!gesture.touch && this.#track.hasPointerCapture(gesture.id)) this.#track.releasePointerCapture(gesture.id);
    const dx = gesture.lastX - gesture.x;
    const fast = performance.now() - gesture.lastTime < 100 && Math.abs(gesture.velocity) > 0.35;
    const deliberate = Math.abs(dx) >= Math.min(48, gesture.width * 0.12) || (fast && Math.abs(dx) >= 12);
    const target = event.type === 'pointerup' && gesture.moved && deliberate
      ? gesture.base - Math.sign(dx)
      : Math.round(this.#position);
    if (gesture.moved) this.#clickUntil = performance.now() + 500;
    this.#resumeAt = performance.now() + 8000;
    if (gesture.moved || Math.abs(this.#position - target) > 0.001) this.#move(target, true, gesture.moved);
    this.resume();
  };

  #click = (event) => {
    if (event.detail === 0 || performance.now() >= this.#clickUntil) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #key = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input, select, textarea')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.#resumeAt = performance.now() + 8000;
    this.#move(Math.round(this.#position) + (event.key === 'ArrowRight' ? 1 : -1), true, true);
    this.resume();
  };

  destroy() {
    this.suspend();
    cancelAnimationFrame(this.#frame);
    this.#controller.abort();
    this.#gesture = null;
    for (const slide of this.#slides) {
      slide.style.removeProperty('transform');
      slide.inert = false;
    }
    for (const attr of ['data-circular-hero', 'dragging', 'transitioning']) this.#host.removeAttribute(attr);
    if (this.#originalTabIndex === null) this.#track.removeAttribute('tabindex');
    else this.#track.setAttribute('tabindex', this.#originalTabIndex);
    if (this.#originalLive === null) this.#host.removeAttribute('aria-live');
    else this.#host.setAttribute('aria-live', this.#originalLive);
  }
}


export class Slideshow extends Component {
  #circularHero;
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      if (this.#circularHero) {
        this.#circularHero.select(parseInt(newValue, 10) || 0, undefined, { animate: false });
        return;
      }
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount >= 3 && this.closest('[id$="__slideshow_E4Kg37"]')) {
      this.#circularHero = new CircularHero(this);
      return;
    }
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    this.#circularHero?.destroy();
    this.#circularHero = undefined;
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.removeEventListener('touchstart', this.#handleTouchStart);
      this.removeEventListener('touchend', this.#handleTouchEnd);
      this.removeEventListener('touchcancel', this.#handleTouchEnd);
      this.removeEventListener('click', this.#handleTouchClick, true);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    this.suspend();
    this.#touchStart = null;
    this.#queuedTouchWrap = null;
    this.#suppressTouchClickUntil = 0;
    this.#interactionResumeAt = 0;

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#circularHero) return this.#circularHero.select(input, event, options);
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;
    if (event?.type === 'click' && event.detail !== 0 && performance.now() < this.#suppressTouchClickUntil) {
      event.preventDefault();
      return;
    }

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        // A revealed slide keeps its aria-hidden state only while it is the current slide and is no
        // longer hidden (a variant image the product card just chose). Every other revealed slide
        // leaves the slideshow; a selection that moves to it exposes it again below.
        if (slide.hasAttribute('hidden') || slide !== currentSlide) slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
        this.#disabled = false;
        this.#completeTouchWrap();

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#circularHero) return this.#circularHero.play();
    if (this.#interval || this.#resumeTimer) return;

    this.paused = false;

    const advance = () => {
      if (this.#hoverPausesPlayback || document.hidden) return;
      if (this.#resumeAfterInteraction && (this.#dragging || this.#touchStart || this.hasAttribute('transitioning'))) return;
      if (this.#resumeAfterInteraction && performance.now() < this.#interactionResumeAt) return;

      this.next();
    };

    const remaining = this.#resumeAfterInteraction ? this.#interactionResumeAt - performance.now() : 0;
    if (remaining > 0) {
      this.#resumeTimer = setTimeout(() => {
        this.#resumeTimer = undefined;
        advance();
        this.#interval = setInterval(advance, interval);
      }, remaining);
    } else {
      this.#interval = setInterval(advance, interval);
    }
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    if (this.#circularHero) return this.#circularHero.pause();
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    if (this.#circularHero) return this.#circularHero.suspend();
    clearInterval(this.#interval);
    this.#interval = undefined;
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (this.#circularHero) return this.#circularHero.resume();
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #resumeTimer = undefined;
  #interactionResumeAt = 0;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.#handleTouchEnd, { passive: true });
    this.addEventListener('click', this.#handleTouchClick, true);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? this.#startEdge(scroller, axis) : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  /**
   * The viewport coordinate of the scroller's start edge: its left edge, or its
   * right edge when a horizontal slideshow renders right-to-left.
   * @param {HTMLElement} scroller - The scroller element.
   * @param {'x' | 'y'} axis - The scroll axis.
   * @returns {number}
   */
  #startEdge(scroller, axis) {
    const rect = scroller.getBoundingClientRect();
    if (axis === 'x' && isRTL(scroller)) return rect.right;

    return rect[axis];
  }

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (event.button !== 0) return;
    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];
    const startIndex = this.current;
    // Pointer positions are physical; in RTL a horizontal slideshow advances leftwards.
    const directionMultiplier = axis === 'x' ? getDirectionMultiplier(this) : 1;

    const controller = new AbortController();
    const { signal } = controller;
    const clickController = new AbortController();
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!moved && (!initialDelta || Math.abs(initialDelta) < DRAG_THRESHOLD)) return;

      if (!moved) {
        const movingBackward = initialDelta * directionMultiplier < 0;
        const movingForward = initialDelta * directionMultiplier > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingBackward && this.atStart) || (movingForward && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        moved = true;
        this.#dragging = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal: clickController.signal });

        this.#resumeAfterInteraction ? this.suspend() : this.pause();
        this.setAttribute('dragging', '');
        this.#scroll.snap = false;
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      if (event.type !== 'pointerup') clickController.abort();
      this.#dragging = false;

      if (!moved) return;

      const { current, slides } = this;
      const { scroller } = this.refs;

      if (!slides?.length || !scroller) return;

      const swipeDistance = (startPosition - previous) * directionMultiplier;
      const wrapForward = startIndex === slides.length - 1 && swipeDistance > 40;
      const wrapBackward = startIndex === 0 && swipeDistance < -40;
      if (event.type === 'pointerup' && this.#resumeAfterInteraction && this.infinite && (wrapForward || wrapBackward)) {
        this.removeAttribute('dragging');
        this.releasePointerCapture(event.pointerId);
        this.#scroll.snap = true;
        await this.select(wrapForward ? slides.length : -1, event);
        await this.#scroll.finished;
        if (!this.#dragging) this.#resumeAfterManualInteraction();
        return;
      }

      const direction = Math.sign(velocity) * directionMultiplier;
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.#resumeAfterManualInteraction();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  get #resumeAfterInteraction() {
    return this.autoplay && this.closest('.slideshow-section') !== null;
  }

  #resumeAfterManualInteraction() {
    if (this.#resumeAfterInteraction) this.#interactionResumeAt = performance.now() + 8000;
    this.resume();
  }

  get #hoverPausesPlayback() {
    return (
      this.matches(':hover') &&
      (!this.#resumeAfterInteraction || window.matchMedia('(hover: hover) and (pointer: fine)').matches)
    );
  }

  #handleMouseEnter = () => {
    if (!this.#resumeAfterInteraction || this.#hoverPausesPlayback) this.suspend();
  };

  #touchStart = null;
  #queuedTouchWrap = null;
  #suppressTouchClickUntil = 0;

  #handleTouchClick = (event) => {
    if (event.detail === 0 || performance.now() >= this.#suppressTouchClickUntil) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #completeTouchWrap() {
    if (this.#disabled || !this.#queuedTouchWrap) return;
    const { direction, origin, target } = this.#queuedTouchWrap;
    this.#queuedTouchWrap = null;
    const slides = this.slides;
    const { scroller } = this.refs;
    if (!slides?.includes(origin) || !slides.includes(target) || !this.#scroll) return;
    if (Math.abs(scroller.scrollLeft - target.offsetLeft) <= 2) {
      this.current = slides.indexOf(target);
      return;
    }
    this.current = slides.indexOf(origin);
    this.#scroll.to(origin, { instant: true });
    this.select(direction > 0 ? slides.length : -1);
  }

  #handleTouchStart = (event) => {
    if (!this.#resumeAfterInteraction) return;
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    const { scroller } = this.refs;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const tolerance = Math.min(64, scroller.clientWidth * 0.15);
    this.#queuedTouchWrap = null;
    this.#suppressTouchClickUntil = 0;
    this.#touchStart = touch && maxScroll > 0 ? {
      x: touch.clientX,
      y: touch.clientY,
      atFirst: scroller.scrollLeft <= tolerance,
      atLast: scroller.scrollLeft >= maxScroll - tolerance,
    } : null;
    this.suspend();
  };

  #handleTouchEnd = (event) => {
    if (!this.#resumeAfterInteraction) return;
    const start = this.#touchStart;
    this.#touchStart = null;
    const touch = event.changedTouches[0];
    const slides = this.slides;

    if (event.type === 'touchend' && event.touches.length === 0 && start && touch && slides?.length && this.infinite) {
      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;
      if (Math.abs(deltaX) > 32 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) {
        this.#suppressTouchClickUntil = performance.now() + 500;
        if (start.atLast && deltaX < 0) {
          this.#queuedTouchWrap = { direction: 1, origin: slides[slides.length - 1], target: slides[0] };
        } else if (start.atFirst && deltaX > 0) {
          this.#queuedTouchWrap = { direction: -1, origin: slides[0], target: slides[slides.length - 1] };
        }
        this.#completeTouchWrap();
      }
    }

    if (event.touches.length === 0) this.#resumeAfterManualInteraction();
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // If the IntersectionObserver reports zero visible slides, then the slideshow has intersected, but no slides meet the visibility threshold.
    // Probably that the slideshow's host is mid-animation or zero-layout, like in an animating modal.
    // Don't stamp aria-hidden="true" on every slide. Set the current slide to the first visible slide.
    if (visibleSlides.length === 0) return 0;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}





